/**
 * Timeline Filters Types and Constants
 *
 * Consolidates all types and constants used by the TimelineFilters component
 * and its sub-components.
 */

import type { FilterOption } from "@/lib/types/component-patterns";
import { MessageSquare, FileText, Briefcase, Calendar, CheckSquare, File } from "lucide-react";

/**
 * Timeline filter configuration
 */
export interface TimelineFilters {
  /** Search query string */
  search?: string;
  /** Array of content types to filter by */
  type?: string[];
  /** Array of statuses to filter by */
  status?: string[];
  /** Array of communication directions to filter by */
  direction?: string[];
  /** Start date for date range filter (ISO string) */
  dateFrom?: string;
  /** End date for date range filter (ISO string) */
  dateTo?: string;
}

/**
 * Timeline sort configuration
 */
export interface TimelineSortOptions {
  /** Field to sort by */
  field: "occurred_at" | "created_at" | "title" | "type";
  /** Sort direction */
  direction: "asc" | "desc";
}

/**
 * Type filter options
 */
export const TYPE_OPTIONS: FilterOption[] = [
  { value: "communication", label: "Communications", icon: MessageSquare },
  { value: "provider_message", label: "Email/Messages", icon: MessageSquare },
  { value: "contract", label: "Contracts", icon: FileText },
  { value: "proposal", label: "Proposals", icon: Briefcase },
  { value: "note", label: "Notes", icon: MessageSquare },
  { value: "meeting", label: "Meetings", icon: Calendar },
  { value: "task", label: "Tasks", icon: CheckSquare },
  { value: "file", label: "Files", icon: File },
];

/**
 * Status filter options
 */
export const STATUS_OPTIONS: FilterOption[] = [
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "draft", label: "Draft" },
  { value: "viewed", label: "Viewed" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "scheduled", label: "Scheduled" },
  { value: "cancelled", label: "Cancelled" },
];

/**
 * Direction filter options
 */
export const DIRECTION_OPTIONS: FilterOption[] = [
  { value: "inbound", label: "Inbound" },
  { value: "outbound", label: "Outbound" },
];

/**
 * Sort field options
 */
export const SORT_OPTIONS: FilterOption[] = [
  { value: "occurred_at", label: "Date" },
  { value: "created_at", label: "Created" },
  { value: "title", label: "Title" },
  { value: "type", label: "Type" },
];

/**
 * Default sort configuration
 */
export const DEFAULT_SORT: TimelineSortOptions = {
  field: "occurred_at",
  direction: "desc",
};

/**
 * Default filters configuration
 */
export const DEFAULT_FILTERS: TimelineFilters = {
  search: undefined,
  type: undefined,
  status: undefined,
  direction: undefined,
  dateFrom: undefined,
  dateTo: undefined,
};
